import { historicalProductName, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { useMemo, useState } from "react";
import { Modal } from "@/components/base/Modal";
import { volumetricWeightKg, chargeableWeightKg } from "@/lib/shipping";
import { buildParcelPlan, type ParcelItemInput, type ParcelConstraintSet } from "@/lib/parcelization";
import { evaluateAutoPackReadiness } from "@/lib/auto-pack-readiness";
import {
  resolveShippingFacts,
  aggregatePackageFacts,
  missingShippingFields,
  isCompleteShipping,
  shippingFieldLabel,
} from "@/lib/shipping-facts";

export interface PackageItem {
  fulfilment_item_id?: string;
  product_name: string;
  qty: number;
}

export interface PackageRow {
  id: string;
  package_number: string;
  weight: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  declared_value: number | null;
  items: PackageItem[] | null;
  tracking_number: string | null;
}

export interface ParcelType {
  id: string;
  name: string;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  max_weight_kg: number | null;
}

export interface FulfilmentItem extends Omit<CatalogueHistoryFields, "variants"> {
  id: string;
  product_id: string;
  variant_id: string | null;
  qty_fulfilled: number;
  products?: {
    name: string;
    sku: string;
    weight_kg: number | null;
    length_cm: number | null;
    width_cm: number | null;
    height_cm: number | null;
  } | null;
  variants?: {
    weight_kg: number | null;
    length_cm: number | null;
    width_cm: number | null;
    height_cm: number | null;
  } | null;
}

export interface PackageDraft {
  package_number: string;
  weight: string;
  length_cm: string;
  width_cm: string;
  height_cm: string;
  declared_value: string;
  tracking_number: string;
}

interface PackagesSectionProps {
  packages: PackageRow[];
  parcelTypes: ParcelType[];
  fulfilmentItems: FulfilmentItem[];
  saving: boolean;
  onSave: (draft: PackageDraft, items: PackageItem[], editingId: string | null) => Promise<boolean>;
  onDelete: (id: string) => void;
  /** Carrier/service parcel constraints driving auto-pack (config-driven). */
  autoPackConstraints?: ParcelConstraintSet | null;
}

function toShippingDimensions(
  facts?: {
    weight_kg: number | null;
    length_cm: number | null;
    width_cm: number | null;
    height_cm: number | null;
  } | null,
): { weightKg: number | null; lengthCm: number | null; widthCm: number | null; heightCm: number | null } | null {
  if (!facts) return null;
  return {
    weightKg: facts.weight_kg,
    lengthCm: facts.length_cm,
    widthCm: facts.width_cm,
    heightCm: facts.height_cm,
  };
}

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

const emptyDraft: PackageDraft = {
  package_number: "",
  weight: "",
  length_cm: "",
  width_cm: "",
  height_cm: "",
  declared_value: "",
  tracking_number: "",
};

// Total qty of a fulfilment item already packed into other packages.
function packedElsewhere(packages: PackageRow[], fulfilmentItemId: string, excludeId: string | null): number {
  return packages.reduce((sum, p) => {
    if (excludeId && p.id === excludeId) return sum;
    return sum + ((p.items ?? []).find((it) => it.fulfilment_item_id === fulfilmentItemId)?.qty ?? 0);
  }, 0);
}

export function PackagesSection({ packages, parcelTypes, fulfilmentItems, saving, onSave, onDelete, autoPackConstraints }: PackagesSectionProps) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PackageDraft>(emptyDraft);
  const [items, setItems] = useState<Record<string, number>>({});
  const [error, setError] = useState("");
  const [autoPacking, setAutoPacking] = useState(false);

  const remainingLines = useMemo(
    () =>
      fulfilmentItems.map((fi) => ({
        id: fi.id,
        name: historicalProductName(fi),
        remainingQty: Math.max(fi.qty_fulfilled - packedElsewhere(packages, fi.id, null), 0),
        facts: resolveShippingFacts(toShippingDimensions(fi.variants), toShippingDimensions(fi.products)),
      })),
    [fulfilmentItems, packages],
  );
  const readiness = useMemo(() => evaluateAutoPackReadiness(remainingLines), [remainingLines]);
  const cardFeedback = error || readiness.feedback;
  const autoPackBlocked = !readiness.canAutoPack;

  const autoPack = async () => {
    setAutoPacking(true);
    setError("");
    if (!readiness.canAutoPack) {
      setError(readiness.feedback ?? "");
      setAutoPacking(false);
      return;
    }
    const constraints: ParcelConstraintSet = autoPackConstraints ?? {
      dimensionalWeightEnabled: true,
      volumetricDivisor: 5000,
    };
    const nameById = new Map(fulfilmentItems.map((fi) => [fi.id, fi.product_name_snapshot ?? fi.products?.name ?? fi.product_id]));

    const remaining = remainingLines.filter((r) => r.remainingQty > 0);

    if (remaining.length === 0) {
      setError("No remaining items to pack.");
      setAutoPacking(false);
      return;
    }

    // Never auto-pack items whose facts are incomplete — that would produce
    // dead-weight-only parcels. Require manual entry or a catalogue update.
    const incomplete = remaining.filter((r) => !isCompleteShipping(r.facts));
    if (incomplete.length > 0) {
      const detail = incomplete
        .map((r) => `${r.name} (missing ${missingShippingFields(r.facts).map(shippingFieldLabel).join(", ")})`)
        .join("; ");
      setError(`Shipping information incomplete — can't auto-pack. ${detail}. Enter package weight/dimensions manually, or update the catalogue.`);
      setAutoPacking(false);
      return;
    }

    const sourceItems: ParcelItemInput[] = remaining.map((r) => ({
      id: r.id,
      quantity: r.remainingQty,
      weightKg: r.facts.weightKg as number,
      lengthCm: r.facts.lengthCm as number,
      widthCm: r.facts.widthCm as number,
      heightCm: r.facts.heightCm as number,
    }));

    const plan = buildParcelPlan(sourceItems, constraints);
    if (!plan.ok) {
      setError(plan.failures.length ? `Couldn't auto-pack: ${plan.failures[0].reason}` : "Couldn't auto-pack these items within the carrier limits.");
      setAutoPacking(false);
      return;
    }

    for (const parcel of plan.parcels) {
      const draft: PackageDraft = {
        package_number: parcel.parcelNumber,
        weight: String(parcel.grossWeightKg),
        length_cm: String(parcel.lengthCm),
        width_cm: String(parcel.widthCm),
        height_cm: String(parcel.heightCm),
        declared_value: "",
        tracking_number: "",
      };
      const packageItems: PackageItem[] = parcel.items.map((it) => ({
        fulfilment_item_id: it.id,
        product_name: nameById.get(it.id) ?? it.id,
        qty: it.quantity,
      }));
      const ok = await onSave(draft, packageItems, null);
      if (!ok) {
        setError("Auto-pack stopped partway through — some packages were already created.");
        setAutoPacking(false);
        return;
      }
    }
    setAutoPacking(false);
  };

  const openAdd = () => {
    setEditingId(null);
    const initial: Record<string, number> = {};
    fulfilmentItems.forEach((fi) => {
      const alreadyPacked = packedElsewhere(packages, fi.id, null);
      const remaining = fi.qty_fulfilled - alreadyPacked;
      if (remaining > 0) initial[fi.id] = remaining;
    });
    setItems(initial);
    setError("");
    setOpen(true);

    // Auto-prepare package facts from the catalogue for the pre-assigned items,
    // so the operator reviews/edits rather than retyping known facts.
    const facts = aggregatePackageFacts(
      fulfilmentItems
        .filter((fi) => (initial[fi.id] ?? 0) > 0)
        .map((fi) => ({ facts: resolveShippingFacts(toShippingDimensions(fi.variants), toShippingDimensions(fi.products)), quantity: initial[fi.id] ?? 0 })),
    );
    setDraft({
      ...emptyDraft,
      package_number: `PKG-${String(packages.length + 1).padStart(3, "0")}`,
      weight: facts.weightKg != null ? String(facts.weightKg) : "",
      length_cm: facts.lengthCm != null ? String(facts.lengthCm) : "",
      width_cm: facts.widthCm != null ? String(facts.widthCm) : "",
      height_cm: facts.heightCm != null ? String(facts.heightCm) : "",
    });
  };

  const openEdit = (p: PackageRow) => {
    setEditingId(p.id);
    setDraft({
      package_number: p.package_number,
      weight: p.weight != null ? String(p.weight) : "",
      length_cm: p.length_cm != null ? String(p.length_cm) : "",
      width_cm: p.width_cm != null ? String(p.width_cm) : "",
      height_cm: p.height_cm != null ? String(p.height_cm) : "",
      declared_value: p.declared_value != null ? String(p.declared_value) : "",
      tracking_number: p.tracking_number ?? "",
    });
    const initial: Record<string, number> = {};
    (p.items ?? []).forEach((it) => {
      const match = fulfilmentItems.find((fi) => fi.id === it.fulfilment_item_id);
      if (match) initial[match.id] = it.qty;
    });
    setItems(initial);
    setError("");
    setOpen(true);
  };

  const applyParcelType = (id: string) => {
    const pt = parcelTypes.find((x) => x.id === id);
    if (!pt) return;
    setDraft((d) => ({
      ...d,
      length_cm: pt.length_cm != null ? String(pt.length_cm) : d.length_cm,
      width_cm: pt.width_cm != null ? String(pt.width_cm) : d.width_cm,
      height_cm: pt.height_cm != null ? String(pt.height_cm) : d.height_cm,
    }));
  };

  const autoFillFromItems = () => {
    const assigned = fulfilmentItems.filter((fi) => (items[fi.id] ?? 0) > 0);
    if (assigned.length === 0) return;
    const facts = aggregatePackageFacts(
      assigned.map((fi) => ({ facts: resolveShippingFacts(toShippingDimensions(fi.variants), toShippingDimensions(fi.products)), quantity: items[fi.id] ?? 0 })),
    );
    setDraft((d) => ({
      ...d,
      weight: facts.weightKg != null ? String(facts.weightKg) : d.weight,
      length_cm: facts.lengthCm != null ? String(facts.lengthCm) : d.length_cm,
      width_cm: facts.widthCm != null ? String(facts.widthCm) : d.width_cm,
      height_cm: facts.heightCm != null ? String(facts.heightCm) : d.height_cm,
    }));
  };

  const submit = async () => {
    if (!draft.package_number.trim()) {
      setError("Package number is required.");
      return;
    }
    const itemList: PackageItem[] = fulfilmentItems
      .filter((fi) => (items[fi.id] ?? 0) > 0)
      .map((fi) => ({
        fulfilment_item_id: fi.id,
        product_name: fi.product_name_snapshot ?? fi.products?.name ?? fi.product_id,
        qty: items[fi.id],
      }));
    const ok = await onSave(draft, itemList, editingId);
    if (ok) setOpen(false);
  };

  const remainingFor = (fi: FulfilmentItem) => {
    const elsewhere = packedElsewhere(packages, fi.id, editingId);
    return Math.max(fi.qty_fulfilled - elsewhere, 0);
  };

  // Assigned items whose catalogue shipping facts are incomplete (for the modal warning).
  const incompleteAssigned = fulfilmentItems
    .filter((fi) => (items[fi.id] ?? 0) > 0)
    .map((fi) => ({
      name: historicalProductName(fi),
      missing: missingShippingFields(resolveShippingFacts(toShippingDimensions(fi.variants), toShippingDimensions(fi.products))),
    }))
    .filter((l) => l.missing.length > 0);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Parcels &amp; packaging</h3>
          <p className="text-xs text-slate-400 mt-0.5">Weights, dimensions and assigned items</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={autoPack}
            disabled={saving || autoPacking || autoPackBlocked}
            aria-label="Auto-pack items"
            aria-describedby={cardFeedback ? "auto-pack-feedback" : undefined}
            className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
            title={autoPackBlocked ? (readiness.feedback ?? "Auto-pack is unavailable") : "Split remaining items into valid parcels using the carrier's limits"}
          >
            <i className="ri-archive-line" /> {autoPacking ? "Packing…" : "Auto-pack items"}
          </button>
          <button
            onClick={openAdd}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-add-line" /> Add package
          </button>
        </div>
      </div>

      {cardFeedback && (
        <div id="auto-pack-feedback" role="alert" className="mb-3 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
          {cardFeedback}
        </div>
      )}

      {packages.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">
          No packages yet. Add a package with weight and dimensions to enable rate calculation.
        </div>
      ) : (
        <div className="space-y-2">
          {packages.map((p) => {
            const vol = volumetricWeightKg(p);
            const charge = chargeableWeightKg(p);
            return (
              <div key={p.id} className="border border-slate-100 rounded-lg p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-slate-800">{p.package_number}</span>
                    {p.tracking_number && <span className="text-xs font-mono text-slate-400">{p.tracking_number}</span>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openEdit(p)} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100" title="Edit">
                      <i className="ri-edit-line" />
                    </button>
                    <button onClick={() => onDelete(p.id)} disabled={saving} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500" title="Remove">
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span>Dead weight: {p.weight != null ? `${p.weight} kg` : "—"}</span>
                  <span>
                    Dims: {p.length_cm && p.width_cm && p.height_cm ? `${p.length_cm}×${p.width_cm}×${p.height_cm} cm` : "—"}
                  </span>
                  <span>Volumetric: {vol > 0 ? `${vol.toFixed(2)} kg` : "—"}</span>
                  <span className="font-medium text-slate-700">Chargeable: {charge > 0 ? `${charge.toFixed(2)} kg` : "—"}</span>
                  {p.declared_value != null && <span>Declared: ${p.declared_value}</span>}
                </div>
                {(p.items ?? []).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(p.items ?? []).map((it, i) => (
                      <span key={i} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                        {it.product_name} ×{it.qty}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editingId ? "Edit Package" : "Add Package"}
        footer={
          <>
            <button onClick={() => setOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button onClick={submit} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Saving..." : "Save Package"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

          {incompleteAssigned.length > 0 && (
            <div className="px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-lg text-sm text-amber-700">
              <p className="font-medium flex items-center gap-1.5">
                <i className="ri-information-line" /> Shipping information incomplete
              </p>
              <ul className="mt-1.5 space-y-0.5 text-xs list-disc list-inside">
                {incompleteAssigned.map((l, i) => (
                  <li key={i}>{l.name}: missing {l.missing.map(shippingFieldLabel).join(", ")}</li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-amber-600">Enter weight/dimensions here, or update the product catalogue shipping details.</p>
            </div>
          )}

          {parcelTypes.length > 0 && (
            <div>
              <label className={labelCls}>Parcel type (quick fill)</label>
              <select onChange={(e) => applyParcelType(e.target.value)} defaultValue="" className={inputCls}>
                <option value="">— Select a template —</option>
                {parcelTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>
                    {pt.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Package number *</label>
              <input value={draft.package_number} onChange={(e) => setDraft((d) => ({ ...d, package_number: e.target.value }))} className={inputCls} placeholder="PKG-001" />
            </div>
            <div>
              <label className={labelCls}>Tracking number</label>
              <input value={draft.tracking_number} onChange={(e) => setDraft((d) => ({ ...d, tracking_number: e.target.value }))} className={inputCls} />
            </div>
          </div>

          {fulfilmentItems.length > 0 && (
            <button
              type="button"
              onClick={autoFillFromItems}
              className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap"
            >
              <i className="ri-magic-line" /> Auto-fill weight &amp; dims from assigned items
            </button>
          )}

          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>Weight (kg)</label>
              <input type="number" step="0.01" value={draft.weight} onChange={(e) => setDraft((d) => ({ ...d, weight: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Length (cm)</label>
              <input type="number" step="0.1" value={draft.length_cm} onChange={(e) => setDraft((d) => ({ ...d, length_cm: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Width (cm)</label>
              <input type="number" step="0.1" value={draft.width_cm} onChange={(e) => setDraft((d) => ({ ...d, width_cm: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Height (cm)</label>
              <input type="number" step="0.1" value={draft.height_cm} onChange={(e) => setDraft((d) => ({ ...d, height_cm: e.target.value }))} className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Declared value</label>
            <input type="number" step="0.01" value={draft.declared_value} onChange={(e) => setDraft((d) => ({ ...d, declared_value: e.target.value }))} className={inputCls} />
          </div>

          {fulfilmentItems.length > 0 && (
            <div>
              <label className={labelCls}>Assign items to this package</label>
              <div className="space-y-1.5 border border-slate-100 rounded-lg p-3 max-h-40 overflow-y-auto">
                {fulfilmentItems.map((fi) => {
                  const remaining = remainingFor(fi);
                  return (
                    <div key={fi.id} className="flex items-center gap-3 text-sm">
                      <span className="flex-1 text-slate-700">
                        {historicalProductName(fi)} <span className="text-xs text-slate-400 font-mono">{fi.sku_snapshot ?? fi.products?.sku}</span>
                      </span>
                      <input
                        type="number"
                        min="0"
                        max={remaining}
                        disabled={remaining <= 0}
                        value={items[fi.id] ?? 0}
                        onChange={(e) => setItems((m) => ({ ...m, [fi.id]: Number(e.target.value) }))}
                        className="w-20 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-sm disabled:opacity-50"
                      />
                      <span className="text-xs text-slate-400 whitespace-nowrap">of {remaining}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
